'use client';

import { useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/providers/auth-provider';
import { useTranslation } from '@/i18n';

interface LocaleOption {
  code: string;
  label: string;
  flag: string;
}

const LOCALE_OPTIONS: LocaleOption[] = [
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'hu', label: 'Magyar', flag: '🇭🇺' },
  { code: 'bg', label: 'Български', flag: '🇧🇬' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

export function LocalePickerModal() {
  const { t } = useTranslation();
  const { refreshProfile } = useAuth();
  const [saving, setSaving] = useState(false);

  // If user already has a locale stored locally, skip the modal — don't force re-selection
  if (typeof window !== 'undefined' && localStorage.getItem('crm_locale')) {
    return null;
  }

  const handleSelect = async (code: string) => {
    setSaving(true);
    try {
      await apiClient.patch('/auth/me', { language: code });
      await refreshProfile();
      // Reload so I18nProvider re-initialises with the selected language
      window.location.reload();
    } catch {
      // If the patch fails, the modal will remain visible — user can retry
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
      data-testid="locale-picker-modal"
    >
      <div className="bg-card rounded-xl shadow-2xl p-6 md:p-8 w-full max-w-sm max-h-[90vh] overflow-y-auto my-auto">
        <h2 className="text-xl font-bold text-center mb-2">{t('common.chooseLanguage')}</h2>
        <p className="text-sm text-muted-foreground text-center mb-6">
          {t('common.chooseLanguageHint')}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {LOCALE_OPTIONS.map((opt) => (
            <button
              key={opt.code}
              type="button"
              disabled={saving}
              onClick={() => handleSelect(opt.code)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50 text-left"
              data-testid={`locale-option-${opt.code}`}
            >
              <span className="text-2xl">{opt.flag}</span>
              <span className="font-medium text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
